/**
 * Compliance API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 */

import { HCVProgram, ComplianceDocument, RentersInsuranceRecord } from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

export interface RentersInsuranceFilters {
  tenantId?: string;
  status?: string;
  propertyId?: string;
}

export const complianceApi = {
  rentersInsurance: {
    list: async (filters?: RentersInsuranceFilters) => {
      const context = getContext();
      return router.get<RentersInsuranceRecord[]>('/compliance/renters-insurance', context, filters as Record<string, string>);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<RentersInsuranceRecord>(`/compliance/renters-insurance/${id}`, context);
    },
    getByTenant: async (tenantId: string) => {
      const context = getContext();
      return router.get<RentersInsuranceRecord | null>(`/compliance/renters-insurance/tenant/${tenantId}`, context);
    },
    create: async (body: { tenantId: string; leaseId?: string; expirationDate: string; documentUrl?: string }) => {
      const context = getContext();
      return router.post<RentersInsuranceRecord>('/compliance/renters-insurance', body, context);
    },
    update: async (id: string, updates: { expirationDate?: string; documentUrl?: string }) => {
      const context = getContext();
      return router.patch<RentersInsuranceRecord>(`/compliance/renters-insurance/${id}`, updates, context);
    },
  },
  hcv: {
    list: async () => {
      const context = getContext();
      return router.get<HCVProgram[]>('/compliance/hcv', context);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<HCVProgram>(`/compliance/hcv/${id}`, context);
    },
    create: async (program: HCVProgram) => {
      const context = getContext();
      return router.post<HCVProgram>('/compliance/hcv', program, context);
    },
    update: async (id: string, updates: Partial<HCVProgram>) => {
      const context = getContext();
      return router.patch<HCVProgram>(`/compliance/hcv/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/compliance/hcv/${id}`, context);
    },
    resolveCompliance: async (body: {
      programId: string;
      resolutionReason: string;
      nextActionDate?: string;
      impactSummary?: string;
    }) => {
      const context = getContext();
      return router.post<HCVProgram>('/compliance/hcv/resolve-compliance', body, context);
    },
    reopenCompliance: async (body: { programId: string; reason: string }) => {
      const context = getContext();
      return router.post<HCVProgram>('/compliance/hcv/reopen-compliance', body, context);
    },
  },
  documents: {
    list: async () => {
      const context = getContext();
      return router.get<ComplianceDocument[]>('/compliance/documents', context);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<ComplianceDocument>(`/compliance/documents/${id}`, context);
    },
    create: async (document: Partial<ComplianceDocument> & { file?: File }) => {
      const context = getContext();
      return router.post<ComplianceDocument>('/compliance/documents', document, context);
    },
    update: async (id: string, updates: Partial<ComplianceDocument>) => {
      const context = getContext();
      return router.patch<ComplianceDocument>(`/compliance/documents/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/compliance/documents/${id}`, context);
    },
  },
};

